import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 10;
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // PayPal redirects with ?subscription_id=I-XXX&token=... ; legacy Stripe used ?session_id=
  const subscriptionId = params.get("subscription_id");
  const sessionId = params.get("session_id");
  const provider = params.get("provider") || (subscriptionId ? "paypal" : "stripe");
  const isPaypal = provider === "paypal" || !!subscriptionId;

  const [state, setState] = useState("checking"); // checking | paid | expired | error | timeout | cancelled | needs_signup
  const [details, setDetails] = useState(null);

  const poll = useCallback(
    async (attempt = 0) => {
      if (isPaypal) {
        if (!subscriptionId) {
          setState("error");
          return;
        }
        if (attempt >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        try {
          const res = await axios.get(`${API}/paypal/subscriptions/${subscriptionId}`);
          setDetails(res.data);
          const status = (res.data.status || "").toUpperCase();
          if (status === "ACTIVE") {
            // Attach to logged-in owner's center OR record as pending for pre-signup flow.
            const jwt = localStorage.getItem("cc_token");
            if (jwt) {
              try {
                await axios.post(
                  `${API}/paypal/subscriptions/${subscriptionId}/attach`,
                  {},
                  { headers: { Authorization: `Bearer ${jwt}` } }
                );
              } catch (e) { /* non-fatal — superadmin can still see ledger */ }
              setState("paid");
            } else {
              // No account yet — record a pending sub then route to signup.
              try {
                await axios.post(`${API}/paypal/pending-subscription`, {
                  subscription_id: subscriptionId,
                  payer_email: res.data.payer_email || null,
                  package_id: sessionStorage.getItem("pending_paypal_tier") || null,
                });
              } catch (e) { /* silent — /register will still lookup by id */ }
              sessionStorage.setItem("pending_paypal_subscription_id", subscriptionId);
              setState("needs_signup");
            }
            return;
          }
          if (status === "CANCELLED" || status === "EXPIRED") {
            setState("cancelled");
            return;
          }
          // APPROVAL_PENDING / APPROVED — keep polling, PayPal needs a moment.
          setTimeout(() => poll(attempt + 1), POLL_MS);
        } catch (err) {
          console.error(err);
          setState("error");
        }
        return;
      }

      // Legacy Stripe path
      if (!sessionId) {
        setState("error");
        return;
      }
      if (attempt >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      try {
        const res = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        setDetails(res.data);
        if (res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (res.data.status === "expired") {
          setState("expired");
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_MS);
      } catch (err) {
        console.error(err);
        setState("error");
      }
    },
    [sessionId, subscriptionId, isPaypal]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  const providerName = isPaypal ? "PayPal" : "Stripe";
  const payerEmail = details?.payer_email || "";

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-stone-50 grid place-items-center px-6">
      <div className="max-w-lg w-full rounded-3xl border border-stone-200 bg-white p-10 shadow-lift text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-10 h-10 mx-auto text-emerald-700 animate-spin" />
            <h1 className="mt-6 font-display text-2xl font-medium text-stone-900">Confirming your subscription…</h1>
            <p className="mt-2 text-stone-500">One moment while we verify with {providerName}.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-paid">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-700 grid place-items-center">
              <CheckCircle2 className="w-7 h-7 text-white" />
            </div>
            <h1 className="mt-6 font-display text-3xl font-medium text-stone-900">Welcome to ChildCheckOh.</h1>
            <p className="mt-3 text-stone-600">
              Subscription active{details?.last_payment_amount ? ` · $${Number(details.last_payment_amount).toFixed(2)}` : ""}.
              Our Ohio onboarding team will email you within one business day to schedule your setup.
            </p>
            {isPaypal && details?.next_billing_time && (
              <p className="mt-2 text-xs text-stone-400">
                Next billing: {new Date(details.next_billing_time).toLocaleDateString()}
              </p>
            )}
          </div>
        )}

        {state === "needs_signup" && (
          <div data-testid="payment-needs-signup">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-700 grid place-items-center">
              <CheckCircle2 className="w-7 h-7 text-white" />
            </div>
            <h1 className="mt-6 font-display text-3xl font-medium text-stone-900">Payment received.</h1>
            <p className="mt-3 text-stone-600">
              Your PayPal subscription is <b>active</b>. One last step — create your ChildCheckOh account so we can wire the subscription to your center.
            </p>
            <Button
              data-testid="payment-continue-signup-btn"
              onClick={() => {
                const qp = new URLSearchParams({ paypal_subscription_id: subscriptionId });
                if (payerEmail) qp.set("email", payerEmail);
                navigate(`/register?${qp.toString()}`);
              }}
              className="mt-6 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl px-6 py-5"
            >
              Continue to create your center <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="mt-3 text-xs text-stone-400">
              Payer: {payerEmail || "PayPal-verified email"}
            </p>
          </div>
        )}

        {state === "cancelled" && (
          <div data-testid="payment-cancelled">
            <XCircle className="w-10 h-10 mx-auto text-stone-400" />
            <h1 className="mt-6 font-display text-2xl font-medium text-stone-900">Subscription cancelled</h1>
            <p className="mt-2 text-stone-500">No charge was made. Come back anytime.</p>
          </div>
        )}

        {state === "expired" && (
          <div data-testid="payment-expired">
            <XCircle className="w-10 h-10 mx-auto text-stone-400" />
            <h1 className="mt-6 font-display text-2xl font-medium text-stone-900">Session expired</h1>
            <p className="mt-2 text-stone-500">This checkout session expired. Please start a new one.</p>
          </div>
        )}

        {state === "error" && (
          <div data-testid="payment-error">
            <XCircle className="w-10 h-10 mx-auto text-red-500" />
            <h1 className="mt-6 font-display text-2xl font-medium text-stone-900">Something went wrong</h1>
            <p className="mt-2 text-stone-500">We couldn&apos;t confirm your subscription. Please contact us if you were charged.</p>
          </div>
        )}

        {state === "timeout" && (
          <div data-testid="payment-timeout">
            <Loader2 className="w-10 h-10 mx-auto text-orange-500" />
            <h1 className="mt-6 font-display text-2xl font-medium text-stone-900">Still processing…</h1>
            <p className="mt-2 text-stone-500">
              Your subscription is taking longer than usual to activate. Check your email for a {providerName} confirmation — it usually arrives within a minute.
            </p>
          </div>
        )}

        <Button
          asChild
          data-testid="payment-back-home-btn"
          className="mt-8 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl px-6 py-5"
        >
          <Link to="/">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to ChildCheck
          </Link>
        </Button>
      </div>
    </div>
  );
}
